import React, { Component, ErrorInfo, ReactNode } from 'react';
import html2canvas from 'html2canvas';
import { supportTicketsAPI } from '../../services/portalAPI';
import ErrorToast from './ErrorToast';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  showToast: boolean;
}

// Circular buffer for recent console errors
const consoleErrorBuffer: string[] = [];
const MAX_BUFFER = 20;
let consolePatched = false;

function patchConsoleError() {
  if (consolePatched) return;
  consolePatched = true;
  const originalError = console.error;
  console.error = (...args: any[]) => {
    try {
      const msg = args.map(a => {
        if (typeof a === 'string') return a;
        try { return JSON.stringify(a); } catch { return String(a); }
      }).join(' ');
      consoleErrorBuffer.push(msg);
      if (consoleErrorBuffer.length > MAX_BUFFER) consoleErrorBuffer.shift();
    } catch {
      // ignore
    }
    originalError.apply(console, args);
  };
}

// Rate limit: max 1 auto-ticket per 5 minutes
let lastAutoTicketTime = 0;
const RATE_LIMIT_MS = 5 * 60 * 1000;

class PortalErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, showToast: false };
  }

  componentDidMount() {
    patchConsoleError();
  }

  static getDerivedStateFromError(): State {
    return { hasError: true, showToast: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const now = Date.now();
    if (now - lastAutoTicketTime < RATE_LIMIT_MS) {
      console.error('[ErrorBoundary] Rate limited — skipping auto-ticket');
      return;
    }
    lastAutoTicketTime = now;

    this.captureAndReport(error, errorInfo);
  }

  async captureAndReport(error: Error, errorInfo: ErrorInfo) {
    try {
      // Capture screenshot
      let screenshotBlob: Blob | null = null;
      try {
        const canvas = await html2canvas(document.body, {
          logging: false,
          useCORS: true,
          scale: 0.5,
          height: Math.min(document.body.scrollHeight, 2000),
        });
        screenshotBlob = await new Promise<Blob | null>(resolve =>
          canvas.toBlob(blob => resolve(blob), 'image/png', 0.7)
        );
      } catch (e) {
        console.error('[ErrorBoundary] Screenshot capture failed', e);
      }

      const errorData = {
        message: error.message,
        stack: error.stack?.slice(0, 2000),
        componentStack: errorInfo.componentStack?.slice(0, 2000),
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        recentErrors: [...consoleErrorBuffer],
      };

      const formData = new FormData();
      formData.append('subject', `Auto-captured Error: ${error.message.slice(0, 100)}`);
      formData.append('description', `Unhandled error in portal at ${window.location.pathname}\n\n${error.message}`);
      formData.append('error_data', JSON.stringify(errorData));

      if (screenshotBlob) {
        formData.append('screenshot', screenshotBlob, 'error-screenshot.png');
      }

      await supportTicketsAPI.create(formData);
      this.setState({ showToast: true });
    } catch (e) {
      console.error('[ErrorBoundary] Failed to create auto-ticket', e);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          maxWidth: 500, margin: '80px auto', textAlign: 'center', padding: '24px 16px',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>!</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>
            An unexpected error occurred. A support ticket has been automatically created.
          </p>
          <button
            onClick={() => { window.location.href = '/portal/dashboard'; }}
            style={{
              background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6,
              padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Back to Dashboard
          </button>

          {this.state.showToast && (
            <ErrorToast onDismiss={() => this.setState({ showToast: false })} />
          )}
        </div>
      );
    }

    return (
      <>
        {this.props.children}
        {this.state.showToast && (
          <ErrorToast onDismiss={() => this.setState({ showToast: false })} />
        )}
      </>
    );
  }
}

export default PortalErrorBoundary;
